import { useCallback, useMemo } from 'react';
import {
    keepPreviousData,
    useInfiniteQuery,
    useQuery,
    useQueryClient
} from '@tanstack/react-query';
import {
    fetchDmMessageInbox,
    fetchDmMessagePatient,
    type DmMessageInboxFilters,
    type DmMessageInboxMeta,
    type DmMessageInboxParams,
    type DmMessagePatientRow
} from '../services/dmMessagesInbox';
import { uniqueByPatient } from '../utils/uniqueByPatient';
import { DM_MESSAGES_INBOX_KEY, dmInboxFilterKeyParts } from './queryKeys';
import { invalidateSidebarCounts } from './useSidebarCounts';

const STALE_TIME_MS = 60_000;
// Picks up AI triage written while the patient is open.
const PATIENT_REFRESH_MS = 60_000;
const NO_ROWS: DmMessagePatientRow[] = [];

type InboxFilterParams = Omit<DmMessageInboxParams, 'page'>;

function filterKeyParts(params: InboxFilterParams): string[] {
    return [String(params.limit), ...dmInboxFilterKeyParts(params)];
}

// The prefix covers the list (both modes) and its filter options.
export function useRefreshDmMessagesInbox() {
    const queryClient = useQueryClient();
    return useCallback(async () => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: [DM_MESSAGES_INBOX_KEY] }),
            invalidateSidebarCounts(queryClient)
        ]);
    }, [queryClient]);
}

export function useDmMessageInbox({
    enabled = true,
    ...params
}: DmMessageInboxParams & { enabled?: boolean }) {
    const refresh = useRefreshDmMessagesInbox();
    const { data, isLoading, isPlaceholderData, isError, error } = useQuery({
        queryKey: [DM_MESSAGES_INBOX_KEY, 'paged', params.page, ...filterKeyParts(params)],
        queryFn: () => fetchDmMessageInbox(params),
        staleTime: STALE_TIME_MS,
        placeholderData: keepPreviousData,
        enabled
    });
    // Null until the first response lands, so callers can tell "loading" from "zero".
    const meta: DmMessageInboxMeta | null = data?.meta ?? null;
    return {
        rows: data?.data ?? NO_ROWS,
        meta,
        isLoading,
        isPlaceholderData,
        isError,
        error,
        refresh
    };
}

export function useInfiniteDmMessageInbox({
    enabled = true,
    ...params
}: InboxFilterParams & { enabled?: boolean }) {
    const refresh = useRefreshDmMessagesInbox();
    const query = useInfiniteQuery({
        queryKey: [DM_MESSAGES_INBOX_KEY, 'infinite', ...filterKeyParts(params)],
        initialPageParam: 1,
        queryFn: ({ pageParam }) => fetchDmMessageInbox({ ...params, page: pageParam }),
        getNextPageParam: (lastPage) =>
            lastPage.meta.page < lastPage.meta.totalPages ? lastPage.meta.page + 1 : undefined,
        staleTime: STALE_TIME_MS,
        placeholderData: keepPreviousData,
        enabled
    });
    const { data } = query;
    const rows = useMemo(
        () => (data ? uniqueByPatient(data.pages.flatMap((page) => page.data)) : NO_ROWS),
        [data]
    );
    const meta: DmMessageInboxMeta | null = data?.pages[0]?.meta ?? null;
    return {
        rows,
        meta,
        isLoading: query.isLoading,
        isFetchingNextPage: query.isFetchingNextPage,
        isPlaceholderData: query.isPlaceholderData,
        isError: query.isError,
        error: query.error,
        hasNextPage: query.hasNextPage,
        fetchNextPage: query.fetchNextPage,
        refresh
    };
}

// Under the list's prefix, so a review, reply or assignment refreshes the open patient too.
export function useDmMessagePatient(
    patientId: string,
    filters: DmMessageInboxFilters,
    { enabled = true }: { enabled?: boolean } = {}
) {
    return useQuery({
        queryKey: [DM_MESSAGES_INBOX_KEY, 'patient', patientId, ...dmInboxFilterKeyParts(filters)],
        queryFn: () => fetchDmMessagePatient(patientId, filters),
        staleTime: STALE_TIME_MS,
        refetchInterval: PATIENT_REFRESH_MS,
        enabled: enabled && patientId.length > 0
    });
}
